import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Use current origin for API calls to work on any deployed domain
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

// Cache duration in milliseconds (5 minutes)
const AUTH_CACHE_DURATION = 5 * 60 * 1000;

// Set up axios interceptor to add session token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastAuthCheck = useRef(0);
  const authCheckInProgress = useRef(false);

  useEffect(() => {
    // Check for session_id in URL (from Google OAuth)
    const fullUrl = window.location.href;
    const hash = window.location.hash;
    const search = window.location.search;
    
    console.log('🔍 Auth check - URL:', fullUrl);
    
    let sessionId = null;
    
    // Check hash fragment first (most common)
    if (hash && hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1].split('&')[0].split('#')[0];
      console.log('📍 Found session_id in hash:', sessionId);
    }
    // Check query string
    else if (search && search.includes('session_id=')) {
      sessionId = search.split('session_id=')[1].split('&')[0].split('#')[0];
      console.log('📍 Found session_id in search:', sessionId);
    }
    // Check full URL as fallback
    else if (fullUrl.includes('session_id=')) {
      sessionId = fullUrl.split('session_id=')[1].split('&')[0].split('#')[0];
      console.log('📍 Found session_id in fullUrl:', sessionId);
    }
    
    if (sessionId) {
      handleGoogleCallback(sessionId);
      return;
    }

    // Check for cached user data first
    const cachedUser = localStorage.getItem('cached_user');
    const cacheTime = localStorage.getItem('auth_cache_time');
    
    if (cachedUser && cacheTime) {
      const cacheAge = Date.now() - parseInt(cacheTime, 10);
      if (cacheAge < AUTH_CACHE_DURATION) {
        // Use cached user data
        console.log('✅ Using cached auth data');
        setUser(JSON.parse(cachedUser));
        setLoading(false);
        // Refresh in background (don't block)
        checkAuthBackground();
        return;
      }
    }

    // No valid cache, do full auth check
    checkAuth();
  }, []);

  const handleGoogleCallback = async (sessionId) => {
    console.log('🔐 Processing Google OAuth callback with session:', sessionId);
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        {},
        {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true,
          timeout: 15000
        }
      );
      
      console.log('✅ Session established:', response.data);
      
      if (response.data && response.data.user) {
        setUser(response.data.user);
        cacheUser(response.data.user);
        
        if (response.data.token) {
          localStorage.setItem('session_token', response.data.token);
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, '/dashboard');
      } else {
        throw new Error('Invalid response from session endpoint');
      }
    } catch (error) {
      console.error('❌ Google OAuth error:', error.message);
      // Try fallback auth
      try {
        const fallbackResponse = await axios.get(`${API}/auth/me`, {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true,
          timeout: 10000
        });
        if (fallbackResponse.data) {
          setUser(fallbackResponse.data);
          cacheUser(fallbackResponse.data);
          window.history.replaceState({}, document.title, '/dashboard');
        }
      } catch (fallbackError) {
        console.error('❌ Fallback auth also failed:', fallbackError.message);
        window.history.replaceState({}, document.title, '/');
      }
    } finally {
      setLoading(false);
    }
  };

  // Cache user data locally
  const cacheUser = (userData) => {
    if (userData) {
      localStorage.setItem('cached_user', JSON.stringify(userData));
      localStorage.setItem('auth_cache_time', Date.now().toString());
      lastAuthCheck.current = Date.now();
    }
  };

  // Clear user cache
  const clearUserCache = () => {
    localStorage.removeItem('cached_user');
    localStorage.removeItem('auth_cache_time');
    lastAuthCheck.current = 0;
  };

  // Background auth check (non-blocking)
  const checkAuthBackground = async () => {
    if (authCheckInProgress.current) return;
    
    const timeSinceLastCheck = Date.now() - lastAuthCheck.current;
    if (timeSinceLastCheck < AUTH_CACHE_DURATION) return;
    
    authCheckInProgress.current = true;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      cacheUser(response.data);
    } catch (error) {
      // Session expired, clear cache
      clearUserCache();
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      authCheckInProgress.current = false;
    }
  };

  const checkAuth = async () => {
    // Prevent duplicate checks
    if (authCheckInProgress.current) {
      return;
    }
    
    authCheckInProgress.current = true;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      cacheUser(response.data);
    } catch (error) {
      clearUserCache();
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data.user);
      cacheUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      return { 
        success: true, 
        message: response.data.message,
        verification_required: response.data.verification_required 
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    // Preserve theme preference before clearing storage
    const darkModePreference = localStorage.getItem('lyceum-dark-mode');
    
    try {
      console.log('Logging out...');
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      console.log('Logout API call successful');
      setUser(null);
      clearUserCache();
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore theme preference
      if (darkModePreference) {
        localStorage.setItem('lyceum-dark-mode', darkModePreference);
      }
      
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null);
      clearUserCache();
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore theme preference
      if (darkModePreference) {
        localStorage.setItem('lyceum-dark-mode', darkModePreference);
      }
      
      window.location.href = '/';
    }
  };

  const loginWithGoogle = () => {
    window.location.href = `${API}/auth/google`;
  };

  // Refresh user data (for when user updates profile, etc.)
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      cacheUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      loginWithGoogle,
      refreshUser,
      checkAuth: checkAuthBackground
    }}>
      {children}
    </AuthContext.Provider>
  );
};
